import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Printer } from "lucide-react";

const CAMPOS = [
  { key: "lenguajes", label: "LENGUAJES" },
  { key: "saberes", label: "SABERES Y PENSAMIENTO CIENTÍFICO" },
  { key: "etica", label: "ÉTICA, NATURALEZA Y SOCIEDADES" },
  { key: "humano", label: "DE LO HUMANO Y LO COMUNITARIO" },
];

const TRI_LABEL = { 1: "I", 2: "II", 3: "III" };
const GRADO_ORDINAL = { 1: "1er.", 2: "2do.", 3: "3er.", 4: "4to.", 5: "5to.", 6: "6to." };
const NEM_TO_NUM = { NAE: 5, AE: 7, AS: 8.5, ADS: 10 };

export default function BoletaPrint() {
  const { grupoId } = useParams();
  const [sp] = useSearchParams();
  const triQuery = parseInt(sp.get("trimestre") || "0", 10);
  const alumnoIdQuery = sp.get("alumno");
  const todosQuery = sp.get("todos") === "1";
  const mode = todosQuery ? "todos" : alumnoIdQuery ? "individual" : "grupo";

  const [grupo, setGrupo] = useState(null);
  const [plantel, setPlantel] = useState(null);
  const [director, setDirector] = useState(null);
  const [alumnos, setAlumnos] = useState([]);
  const [califs, setCalifs] = useState([]);
  const [alumnoInd, setAlumnoInd] = useState(null);
  const [triData, setTriData] = useState({ 1: null, 2: null, 3: null });
  const [triDataMap, setTriDataMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const g = (await api.get(`/grupos/${grupoId}`)).data;
        setGrupo(g);
        const p = (await api.get(`/planteles`)).data.find((x) => x.id === g.plantel_id);
        setPlantel(p);
        if (p?.director_user_id) {
          try {
            const us = (await api.get(`/usuarios`)).data.find((u) => u.id === p.director_user_id);
            setDirector(us);
          } catch { /* ignore */ }
        }
        if (mode === "grupo") {
          const [al, cs] = await Promise.all([
            api.get(`/alumnos?grupo_id=${grupoId}`),
            api.get(`/calificaciones?grupo_id=${grupoId}&ciclo=${g.ciclo}&trimestre=${triQuery || 1}`),
          ]);
          setAlumnos(al.data);
          setCalifs(cs.data);
        } else if (mode === "todos") {
          const al = (await api.get(`/alumnos?grupo_id=${grupoId}`)).data;
          setAlumnos(al);
          const results = await Promise.all(
            al.map((a) => api.get(`/calificaciones/alumno/${a.id}?ciclo=${g.ciclo}`).then((r) => [a.id, r.data.trimestres]).catch(() => [a.id, { 1: null, 2: null, 3: null }]))
          );
          const map = {};
          results.forEach(([aid, tris]) => { map[aid] = tris; });
          setTriDataMap(map);
        } else {
          const { data } = await api.get(`/calificaciones/alumno/${alumnoIdQuery}?ciclo=${g.ciclo}`);
          setAlumnoInd(data.alumno);
          setTriData(data.trimestres);
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    /* eslint-disable-next-line */
  }, [grupoId, alumnoIdQuery, triQuery, mode]);

  const nombre = (a) => [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" ").toUpperCase();

  if (loading) return <div className="p-10 text-center text-[#9CA3AF]">Cargando boleta…</div>;
  if (!grupo) return <div className="p-10 text-center text-[#9F2241]">Grupo no encontrado.</div>;

  const escala = plantel?.escala_boletas === "NUMERICA" ? "NUMERICA" : "LETRAS";
  const sorted = [...alumnos].sort((a, b) => nombre(a).localeCompare(nombre(b), "es"));
  const docenteNombre = grupo?.docente ? [grupo.docente.apellido_paterno, grupo.docente.apellido_materno, grupo.docente.nombre].filter(Boolean).join(" ").toUpperCase() : "";
  const directorNombre = director ? [director.apellido_paterno, director.apellido_materno, director.nombre].filter(Boolean).join(" ").toUpperCase() : "";

  return (
    <div className="min-h-screen bg-white p-6 print:p-0" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
      <div className="max-w-[1000px] mx-auto">
        <div className="flex justify-end items-center gap-3 mb-3 print:hidden">
          <span className="text-[10px] text-[#6B7280]">Tip: en el diálogo de impresión desmarca <b>«Encabezados y pies de página»</b> para no imprimir la URL ni la fecha.</span>
          <button onClick={() => window.print()} data-testid="btn-print-boleta"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-[#235B4E] text-white text-xs hover:bg-[#1D4A40]">
            <Printer className="w-3.5 h-3.5" /> Imprimir / Guardar PDF
          </button>
        </div>

        {mode === "grupo" ? (
          <BoletaGrupo grupo={grupo} plantel={plantel} alumnos={sorted} califs={califs}
            trimestre={triQuery || 1} escala={escala}
            docenteNombre={docenteNombre} directorNombre={directorNombre} />
        ) : mode === "todos" ? (
          sorted.map((a) => (
            <div key={a.id} className="boleta-break">
              <BoletaIndividual grupo={grupo} plantel={plantel} alumno={a} triData={triDataMap[a.id] || { 1: null, 2: null, 3: null }}
                escala={escala} docenteNombre={docenteNombre} directorNombre={directorNombre} />
            </div>
          ))
        ) : (
          <BoletaIndividual grupo={grupo} plantel={plantel} alumno={alumnoInd} triData={triData}
            escala={escala} docenteNombre={docenteNombre} directorNombre={directorNombre} />
        )}
      </div>
      <style>{`
        @page { size: Letter ${mode === "grupo" ? "landscape" : "portrait"}; margin: 8mm; }
        .boleta-page { min-height: 258mm; display: flex; flex-direction: column; }
        .boleta-page .obs-fill { flex: 1; }
        .boleta-break + .boleta-break { margin-top: 24px; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          html, body { margin: 0 !important; padding: 0 !important; }
          .boleta-break { page-break-after: always; break-after: page; page-break-inside: avoid; break-inside: avoid; margin-top: 0 !important; }
          .boleta-break:last-child { page-break-after: auto; break-after: auto; }
          .boleta-break + .boleta-break { margin-top: 0 !important; }
          .boleta-break .boleta-page { min-height: 0; height: auto; }
        }
      `}</style>
    </div>
  );
}

function OfficialHeader({ plantel, grupo, showGradoCiclo }) {
  const TAM_LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/jju7uv5d_sep%20tamaulipas.png";
  const SEP_LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/03jykiph_sep.png";
  return (
    <div className="border-b-2 border-black">
      <div className="flex items-center px-3 py-3">
        <div className="w-56 shrink-0">
          <img src={TAM_LOGO} alt="Tamaulipas" className="h-14 object-contain" crossOrigin="anonymous" />
        </div>
        <div className="flex-1 text-center">
          {showGradoCiclo && (
            <>
              <div className="text-[12px] font-bold tracking-widest text-[#374151]">SISTEMA EDUCATIVO NACIONAL TAMAULIPAS</div>
              <div className="text-[22px] font-bold my-1 tracking-wide" style={{ fontFamily: "'Georgia', serif" }}>BOLETA DE EVALUACIÓN</div>
              <div className="text-[12px] text-[#374151]">
                {GRADO_ORDINAL[grupo.grado] || `${grupo.grado}°`} GRADO DE EDUCACIÓN PRIMARIA · CICLO ESCOLAR {grupo.ciclo}
              </div>
            </>
          )}
        </div>
        <div className="w-56 shrink-0 text-right">
          <img src={SEP_LOGO} alt="Educación · SEP" className="h-14 object-contain ml-auto" crossOrigin="anonymous" />
        </div>
      </div>
    </div>
  );
}

function BoletaGrupo({ grupo, plantel, alumnos, califs, trimestre, escala, docenteNombre, directorNombre }) {
  const byId = {}; califs.forEach((c) => { byId[c.alumno_id] = c; });
  const nombre = (a) => [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" ").toUpperCase();
  const totalRows = Math.max(alumnos.length, 25);
  const rows = [...alumnos, ...Array(Math.max(0, totalRows - alumnos.length)).fill(null)];
  const showPromedio = escala === "NUMERICA";
  const promRow = (c) => {
    const vals = CAMPOS.map((cf) => parseFloat(c[cf.key])).filter((v) => !isNaN(v));
    if (!vals.length) return "";
    return (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1);
  };
  return (
    <div className="border-2 border-black">
      <OfficialHeader plantel={plantel} grupo={grupo} showGradoCiclo />
      <div className="px-3 py-1.5 text-[11px] border-b border-black flex flex-wrap gap-x-6 gap-y-0.5">
        <div><b>ESCUELA:</b> {(plantel?.nombre || "").toUpperCase()}</div>
        <div><b>C.C.T.:</b> <span className="font-mono">{plantel?.cct || ""}</span></div>
        <div><b>TURNO:</b> {(plantel?.turno || "").toUpperCase()}</div>
        <div><b>GRADO:</b> {grupo.grado}°</div>
        <div><b>GRUPO:</b> {grupo.letra}</div>
        <div><b>TRIMESTRE:</b> {TRI_LABEL[trimestre] || "—"}</div>
      </div>
      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr>
            <th className="border border-black w-8 p-1" rowSpan={2}>No.</th>
            <th className="border border-black text-left p-1 align-middle" rowSpan={2}>
              NOMBRE DEL ALUMNO
              <div className="text-[9px] font-normal">APELLIDO PATERNO&nbsp;&nbsp;APELLIDO MATERNO&nbsp;&nbsp;NOMBRE(S)</div>
            </th>
            <th className="border border-black w-20 p-1" rowSpan={2}>ASISTENCIAS</th>
            <th colSpan={4} className="border border-black p-1">CAMPOS FORMATIVOS</th>
            {showPromedio && (
              <th className="border border-black w-20 p-1 align-middle" rowSpan={2}>PROMEDIO<div className="text-[9px] font-normal leading-tight">(campos formativos)</div></th>
            )}
          </tr>
          <tr>
            {CAMPOS.map((c) => (
              <th key={c.key} className="border border-black p-1 text-[10px] w-32 leading-tight">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((a, i) => {
            const c = a ? byId[a.id] || {} : {};
            return (
              <tr key={a?.id || `empty-${i}`} className="text-center">
                <td className="border border-black p-1">{i + 1}.</td>
                <td className="border border-black p-1 text-left">{a ? nombre(a) : ""}</td>
                <td className="border border-black p-1">{a && c.asistencias ? c.asistencias : ""}</td>
                {CAMPOS.map((cf) => (
                  <td key={cf.key} className="border border-black p-1 font-bold">{a ? (c[cf.key] || "") : ""}</td>
                ))}
                {showPromedio && (
                  <td className="border border-black p-1 font-bold">{a ? promRow(c) : ""}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <FirmaRow docenteNombre={docenteNombre} directorNombre={directorNombre} />
      <EscalaFoot escala={escala} />
    </div>
  );
}

function BoletaIndividual({ grupo, plantel, alumno, triData, escala, docenteNombre, directorNombre }) {
  if (!alumno) return null;
  const nom = [alumno.apellido_paterno, alumno.apellido_materno, alumno.nombre].filter(Boolean).join(" ").toUpperCase();

  const promedio = (t) => {
    const d = triData[t]; if (!d) return "";
    if (escala === "NUMERICA") {
      const vals = CAMPOS.map((c) => parseFloat(d[c.key])).filter((v) => !isNaN(v));
      if (!vals.length) return "";
      return (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1);
    }
    const vals = CAMPOS.map((c) => NEM_TO_NUM[d[c.key]]).filter((v) => typeof v === "number");
    if (!vals.length) return "";
    return (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1);
  };
  const promedioFinalCampo = (key) => {
    const vals = [1, 2, 3].map((t) => {
      const raw = triData[t]?.[key];
      if (escala === "NUMERICA") { const n = parseFloat(raw); return isNaN(n) ? null : n; }
      return NEM_TO_NUM[raw] ?? null;
    }).filter((v) => v !== null);
    if (!vals.length) return "";
    return (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1);
  };
  const promedioFinal = () => {
    const vals = [1, 2, 3].map((t) => parseFloat(promedio(t))).filter((v) => !isNaN(v));
    if (!vals.length) return "";
    return (vals.reduce((s, v) => s + v, 0) / vals.length).toFixed(1);
  };

  return (
    <div className="boleta-page">
      <OfficialHeader plantel={plantel} grupo={grupo} showGradoCiclo />

      <div className="px-3 py-2.5 text-[12px] border-b border-black">
        <div className="mb-1.5"><b>NOMBRE(S) Y APELLIDOS DE LA ALUMNA O DEL ALUMNO:</b>
          <span className="inline-block border-b border-black min-w-[500px] ml-2 pb-0.5">{nom}</span>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
          <div><b>NOMBRE DE LA ESCUELA:</b>
            <span className="inline-block border-b border-black min-w-[300px] ml-2 pb-0.5">{(plantel?.nombre || "").toUpperCase()}</span>
          </div>
          <div><b>C.C.T.:</b>
            <span className="inline-block border-b border-black min-w-[160px] ml-2 pb-0.5 font-mono">{plantel?.cct || ""}</span>
            <span className="ml-4"><b>TURNO:</b>
              <span className="inline-block border-b border-black min-w-[110px] ml-2 pb-0.5">{(plantel?.turno || "").toUpperCase()}</span>
            </span>
          </div>
          <div><b>CURP:</b>
            <span className="inline-block border-b border-black min-w-[300px] ml-2 pb-0.5 font-mono">{alumno.curp || ""}</span>
          </div>
          <div><b>GRUPO:</b>
            <span className="inline-block border-b border-black min-w-[90px] ml-2 pb-0.5">{grupo.letra}</span>
            <span className="ml-4"><b>GRADO:</b>
              <span className="inline-block border-b border-black min-w-[70px] ml-2 pb-0.5">{grupo.grado}°</span>
            </span>
          </div>
        </div>
      </div>

      <table className="w-full border-collapse text-[12px]" style={{ tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "18%" }} />
          {CAMPOS.map((c) => (
            <col key={c.key} style={{ width: escala === "NUMERICA" ? "16%" : "20.5%" }} />
          ))}
          {escala === "NUMERICA" && <col style={{ width: "18%" }} />}
        </colgroup>
        <thead>
          <tr>
            <th className="border border-black p-2 align-middle" rowSpan={2}>PERIODO DE<br/>EVALUACIÓN</th>
            <th colSpan={4} className="border border-black p-1.5">CAMPOS FORMATIVOS</th>
            {escala === "NUMERICA" && (
              <th className="border border-black p-1 align-middle" rowSpan={2}>PROMEDIO</th>
            )}
          </tr>
          <tr>
            {CAMPOS.map((c) => (
              <th key={c.key} className="border border-black p-1.5 text-[11px] leading-tight">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3].map((t) => (
            <tr key={t} className="text-center" style={{ height: "38px" }}>
              <td className="border border-black p-1 font-bold text-[14px]">{t}°</td>
              {CAMPOS.map((c) => (
                <td key={c.key} className="border border-black p-2 font-bold text-[14px]">{triData[t]?.[c.key] || ""}</td>
              ))}
              {escala === "NUMERICA" && (
                <td className="border border-black p-2 font-bold text-[14px]">{promedio(t)}</td>
              )}
            </tr>
          ))}
          <tr className="text-center bg-[#F3F4F6]" style={{ height: "38px" }}>
            <td className="border border-black p-1 font-bold">PROMEDIO<br/>FINAL</td>
            {CAMPOS.map((c) => (
              <td key={c.key} className="border border-black p-2 font-bold text-[14px]">{escala === "NUMERICA" ? promedioFinalCampo(c.key) : ""}</td>
            ))}
            {escala === "NUMERICA" && (
              <td className="border border-black p-2 font-bold text-[14px]">{promedioFinal()}</td>
            )}
          </tr>
        </tbody>
      </table>

      <table className="w-full border-collapse border-t-0 text-[12px] obs-fill" style={{ tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "18%" }} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th className="border border-black p-1.5">PERIODO DE<br/>EVALUACIÓN</th>
            <th className="border border-black p-1.5">OBSERVACIONES</th>
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3].map((t) => (
            <tr key={t}>
              <td className="border border-black p-1 text-center font-bold align-top text-[14px]">{t}°</td>
              <td className="border border-black p-2 text-[12px] align-top" style={{ minHeight: "70px" }}>
                <div style={{ minHeight: "62px" }}>{triData[t]?.observaciones || ""}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <FirmaRow docenteNombre={docenteNombre} directorNombre={directorNombre} />
      <EscalaFoot escala={escala} />
    </div>
  );
}

function FirmaRow({ docenteNombre, directorNombre }) {
  return (
    <div className="mt-8 mb-4 grid grid-cols-2 gap-16 text-center text-xs px-6">
      <div>
        <div className="border-t border-black pt-1">DOCENTE DEL GRUPO</div>
        <div className="text-[10px] mt-0.5">{docenteNombre || ""}</div>
      </div>
      <div>
        <div className="border-t border-black pt-1">DIRECTOR(A) DEL PLANTEL</div>
        <div className="text-[10px] mt-0.5">{directorNombre || ""}</div>
      </div>
    </div>
  );
}

function EscalaFoot({ escala }) {
  if (escala === "NUMERICA") {
    return <div className="border-t border-black px-3 py-1 text-[9px] text-center">Escala numérica: 5 a 10 (con decimales).</div>;
  }
  return (
    <div className="border-t border-black px-3 py-1 text-[9px] text-center">
      <b>Escala NEM:</b> NAE (Nivel esperado alto) · AE (En vías) · AS (Satisfactorio) · ADS (Alcanzó y superó lo esperado)
    </div>
  );
}
